<script setup>
import { computed, h, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/captain/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/captain/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/captain/assistant/AddNewScenariosDialog.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import {
  ACTION_TYPES,
  actionTypeForScenario,
  isScenarioActionTypeCompatible,
  scenarioRiskLevels,
} from 'dashboard/helper/captain/agentActionTypes';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { checkPermissions } = usePolicy();
const canManageScenarios = computed(() => checkPermissions(['administrator']));
const { formatMessage } = useMessageFormatter();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('captainScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const scenarios = useMapGetter('captainScenarios/getRecords');
const actions = ref([]);
const promptVersions = ref([]);
const promptVersionsMeta = ref({
  total_count: 0,
  page: 1,
  per_page: 25,
  total_pages: 0,
});
const isLoadingMoreHistory = ref(false);
let assistantLoadRequestId = 0;
let historyRequestId = 0;

const hasMoreHistory = computed(
  () =>
    Number(promptVersionsMeta.value.page) <
    Number(promptVersionsMeta.value.total_pages)
);

const mergeUniqueVersions = (currentVersions, nextVersions) => {
  const versions = new Map(
    currentVersions.map(version => [String(version.id), version])
  );
  nextVersions.forEach(version => versions.set(String(version.id), version));
  return Array.from(versions.values());
};

const searchQuery = ref('');

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-n-iris-11 [&_a:not([href^="tool://"])]:text-n-slate-12 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-n-slate-12 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

const actionTypeLabels = computed(() => ({
  [ACTION_TYPES.GUIDANCE]: t(
    'CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.GUIDANCE'
  ),
  [ACTION_TYPES.READ]: t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.READ'),
  [ACTION_TYPES.WRITE]: t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.WRITE'),
  [ACTION_TYPES.HANDOFF]: t(
    'CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.HANDOFF'
  ),
  [ACTION_TYPES.EXTERNAL]: t(
    'CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.EXTERNAL'
  ),
  [ACTION_TYPES.DANGEROUS]: t(
    'CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.DANGEROUS'
  ),
  [ACTION_TYPES.MIXED]: t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.MIXED'),
  [ACTION_TYPES.UNKNOWN]: t(
    'CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPES.UNKNOWN'
  ),
}));

const scenarioActionType = scenario =>
  actionTypeForScenario(scenario, actions.value);
const scenarioActionTypeLabel = scenario =>
  actionTypeLabels.value[scenarioActionType(scenario)];
const historyForScenario = scenarioId =>
  promptVersions.value.filter(
    version =>
      version.source_type === 'scenario' &&
      Number(version.scenario_id) === Number(scenarioId)
  );

const incompatibleScenarios = computed(() =>
  scenarios.value.filter(
    scenario => !isScenarioActionTypeCompatible(scenario, actions.value)
  )
);
const suspendedScenarios = computed(() =>
  scenarios.value.filter(
    scenario =>
      scenario.enabled !== false &&
      scenario.runtime_compatible === false &&
      scenario.unavailable_tool_ids?.length
  )
);

const normalizeScenarioTitle = title =>
  String(title ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{M}]+/gu, ' ')
    .trim();

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(
      match => match[1]
    )
  ),
];

const getScenarioToolIds = scenario => [
  ...new Set(
    [
      ...(Array.isArray(scenario?.tools) ? scenario.tools : []),
      ...getToolsFromInstruction(scenario?.instruction),
    ]
      .map(toolId => String(toolId).trim())
      .filter(Boolean)
  ),
];

// Suggested examples are split by operational purpose. No example mixes two
// effect families in the same scenario.
const scenariosExample = computed(() => [
  {
    id: 1,
    title: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.QUALIFY.TITLE'),
    description: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.QUALIFY.DESCRIPTION'),
    instruction: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.QUALIFY.INSTRUCTION'),
    tools: [],
  },
  {
    id: 2,
    title: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.RECORD.TITLE'),
    description: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.RECORD.DESCRIPTION'),
    instruction: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.RECORD.INSTRUCTION'),
    tools: ['add_private_note', 'add_label_to_conversation'],
  },
  {
    id: 3,
    title: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.HANDOFF.TITLE'),
    description: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.HANDOFF.DESCRIPTION'),
    instruction: t('CAPTAIN.ASSISTANTS.SCENARIOS.EXAMPLES.HANDOFF.INSTRUCTION'),
    tools: ['handoff'],
  },
]);

const availableScenarioExamples = computed(() => {
  const existingScenarioTitles = new Set(
    scenarios.value
      .map(scenario => normalizeScenarioTitle(scenario.title))
      .filter(Boolean)
  );
  const existingToolIds = new Set(scenarios.value.flatMap(getScenarioToolIds));

  return scenariosExample.value.filter(example => {
    const normalizedTitle = normalizeScenarioTitle(example.title);
    const exampleToolIds = getScenarioToolIds(example);
    const isTitleCovered =
      normalizedTitle && existingScenarioTitles.has(normalizedTitle);
    const areAllToolsCovered =
      exampleToolIds.length > 0 &&
      exampleToolIds.every(toolId => existingToolIds.has(toolId));

    return !isTitleCovered && !areAllToolsCovered;
  });
});

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return (
    canManageScenarios.value &&
    uiSettings.value?.show_scenarios_suggestions !== false &&
    availableScenarioExamples.value.length > 0
  );
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const isBulkDeletingScenarios = ref(false);
const bulkDeleteDialog = ref(null);
const hoveredCard = ref(null);
const BULK_DELETE_COMPLETED_STATUSES = new Set(['deleted', 'not_found']);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const bulkDeleteDescription = computed(() =>
  t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.CONFIRM_DESCRIPTION', {
    count: bulkSelectedIds.value.size,
  })
);

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const normalizeScenario = scenario => {
  const {
    action_type: _actionType,
    action_type_compatible: _actionTypeCompatible,
    risk_levels: _riskLevels,
    runtime_compatible: _runtimeCompatible,
    unavailable_tool_ids: _unavailableToolIds,
    ...editableScenario
  } = scenario;

  return {
    ...editableScenario,
    tools: getToolsFromInstruction(scenario.instruction),
  };
};

const validateScenarioActionType = scenario => {
  const normalizedScenario = normalizeScenario(scenario);
  if (isScenarioActionTypeCompatible(normalizedScenario, actions.value)) {
    return normalizedScenario;
  }

  const riskLabels = scenarioRiskLevels(normalizedScenario, actions.value)
    .map(type => actionTypeLabels.value[type] || type)
    .join(', ');
  useAlert(
    t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPE_CONFLICT', {
      types: riskLabels,
    })
  );
  return null;
};

const refreshScenarioHistory = async ({
  targetAssistantId = assistantId.value,
  page = 1,
  append = false,
} = {}) => {
  historyRequestId += 1;
  const requestId = historyRequestId;
  if (append) isLoadingMoreHistory.value = true;
  try {
    const { data } = await CaptainAssistantAPI.promptVersions({
      assistantId: targetAssistantId,
      page,
      perPage: 25,
    });
    const versions = (data.payload || []).filter(
      version => version.source_type === 'scenario'
    );
    if (
      requestId !== historyRequestId ||
      targetAssistantId !== assistantId.value
    ) {
      return;
    }

    promptVersions.value = append
      ? mergeUniqueVersions(promptVersions.value, versions)
      : versions;
    promptVersionsMeta.value = {
      total_count: 0,
      page,
      per_page: 25,
      total_pages: 0,
      ...(data.meta || {}),
    };
  } catch (error) {
    if (
      requestId === historyRequestId &&
      targetAssistantId === assistantId.value
    ) {
      useAlert(
        error?.message || t('CAPTAIN.ASSISTANTS.SCENARIOS.HISTORY.LOAD_ERROR')
      );
    }
  } finally {
    if (requestId === historyRequestId) isLoadingMoreHistory.value = false;
  }
};

const loadMoreScenarioHistory = () => {
  if (!hasMoreHistory.value || isLoadingMoreHistory.value) return;
  refreshScenarioHistory({
    page: Number(promptVersionsMeta.value.page) + 1,
    append: true,
  });
};

const updateScenario = async scenario => {
  const normalizedScenario = validateScenarioActionType(scenario);
  if (!normalizedScenario) return;

  try {
    await store.dispatch('captainScenarios/update', {
      id: normalizedScenario.id,
      assistantId: assistantId.value,
      ...normalizedScenario,
    });
    await refreshScenarioHistory();
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('captainScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

const toggleScenarioEnabled = async id => {
  const scenario = scenarios.value.find(record => record.id === id);
  if (!scenario || !canManageScenarios.value) return;

  try {
    await store.dispatch('captainScenarios/update', {
      id,
      assistantId: assistantId.value,
      enabled: scenario.enabled === false,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR')
    );
  }
};

const requestBulkDeleteScenarios = () => {
  if (
    !canManageScenarios.value ||
    !bulkSelectedIds.value.size ||
    isBulkDeletingScenarios.value
  ) {
    return;
  }

  bulkDeleteDialog.value?.open();
};

const bulkDeleteScenarios = async ids => {
  const idsArray = Array.from(
    new Set(ids || Array.from(bulkSelectedIds.value))
  ).filter(Boolean);
  if (
    !canManageScenarios.value ||
    !idsArray.length ||
    isBulkDeletingScenarios.value
  ) {
    return;
  }

  isBulkDeletingScenarios.value = true;
  try {
    const response = await store.dispatch('captainScenarios/bulkDelete', {
      ids: idsArray,
      assistantId: assistantId.value,
    });
    const results = response?.results || [];
    const failedIds = results.length
      ? results
          .filter(result => !BULK_DELETE_COMPLETED_STATUSES.has(result.status))
          .map(result => result.id)
      : idsArray;
    const completedCount = idsArray.length - failedIds.length;

    bulkSelectedIds.value = new Set(failedIds);
    if (completedCount > 0) {
      await Promise.allSettled([
        store.dispatch('captainScenarios/get', {
          assistantId: assistantId.value,
        }),
        refreshScenarioHistory(),
      ]);
    }

    if (failedIds.length > 0 && completedCount > 0) {
      useAlert(
        t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.PARTIAL_DELETE', {
          deleted: completedCount,
          failed: failedIds.length,
        })
      );
    } else if (failedIds.length > 0) {
      useAlert(
        t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.DELETE_FAILED', {
          failed: failedIds.length,
        })
      );
    } else {
      useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.DELETE_FAILED', {
          failed: idsArray.length,
        })
    );
  } finally {
    isBulkDeletingScenarios.value = false;
  }
};

const confirmBulkDeleteScenarios = async () => {
  await bulkDeleteScenarios();
  bulkDeleteDialog.value?.close();
};

const addScenario = async scenario => {
  const normalizedScenario = validateScenarioActionType(scenario);
  if (!normalizedScenario) return;

  try {
    await store.dispatch('captainScenarios/create', {
      assistantId: assistantId.value,
      ...normalizedScenario,
    });
    await refreshScenarioHistory();
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    await Promise.all(
      availableScenarioExamples.value.map(scenario =>
        store.dispatch('captainScenarios/create', {
          assistantId: assistantId.value,
          ...scenario,
        })
      )
    );
    await refreshScenarioHistory();
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const openTools = () => {
  router.push({
    name: 'captain_account_tools_index',
    params: { accountId: route.params.accountId },
    query: {
      ...route.query,
      assistant_id: String(assistantId.value),
    },
  });
};

const fetchAssistantData = async targetAssistantId => {
  assistantLoadRequestId += 1;
  const requestId = assistantLoadRequestId;
  try {
    const [, , { data }] = await Promise.all([
      store.dispatch('captainScenarios/get', {
        assistantId: targetAssistantId,
      }),
      store.dispatch('captainTools/getTools', {
        assistant_id: targetAssistantId,
      }),
      CaptainAssistantAPI.actions({ assistantId: targetAssistantId }),
      refreshScenarioHistory({ targetAssistantId }),
    ]);

    if (
      requestId !== assistantLoadRequestId ||
      targetAssistantId !== assistantId.value
    ) {
      await store.dispatch('captainScenarios/get', {
        assistantId: assistantId.value,
      });
      return;
    }

    actions.value = data.payload || [];
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTIONS_LOAD_ERROR')
    );
  }
};

watch(
  assistantId,
  targetAssistantId => {
    assistantLoadRequestId += 1;
    historyRequestId += 1;
    actions.value = [];
    promptVersions.value = [];
    promptVersionsMeta.value = {
      total_count: 0,
      page: 1,
      per_page: 25,
      total_pages: 0,
    };
    searchQuery.value = '';
    bulkSelectedIds.value = new Set();
    if (targetAssistantId) fetchAssistantData(targetAssistantId);
  },
  { immediate: true }
);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
        :description="$t('CAPTAIN.ASSISTANTS.SCENARIOS.DESCRIPTION')"
      />
      <div class="mt-7 flex items-start justify-between gap-4">
        <div class="max-w-2xl">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPE_TITLE') }}
          </h3>
          <p class="mt-1 text-sm leading-relaxed text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ACTION_TYPE_DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.MANAGE_TOOLS')"
          icon="i-lucide-wrench"
          slate
          sm
          ghost
          class="shrink-0"
          @click="openTools"
        />
      </div>
      <div class="mt-4 flex flex-col gap-2">
        <div
          v-if="incompatibleScenarios.length"
          class="flex items-start gap-2 rounded-lg bg-n-amber-2 px-3 py-2 text-sm text-n-amber-11"
        >
          <span class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0" />
          <span>
            {{
              t('CAPTAIN.ASSISTANTS.SCENARIOS.LEGACY_CONFLICT_WARNING', {
                count: incompatibleScenarios.length,
              })
            }}
          </span>
        </div>
        <div
          v-if="suspendedScenarios.length"
          class="flex items-start justify-between gap-3 rounded-lg bg-n-amber-2 px-3 py-2 text-sm text-n-amber-11"
        >
          <span>
            {{
              t('CAPTAIN.ASSISTANTS.SCENARIOS.SUSPENDED_WARNING', {
                count: suspendedScenarios.length,
              })
            }}
          </span>
          <Button
            :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.MANAGE_TOOLS')"
            slate
            xs
            ghost
            @click="openTools"
          />
        </div>
      </div>
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedScenarios
          :title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
          :items="availableScenarioExamples"
          @close="closeSuggestedRules"
          @add="addAllExampleScenarios"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-3 justify-between">
              <span class="text-sm text-n-slate-12">
                {{ item.title }}
              </span>
              <Button
                :label="
                  $t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                @click="addScenario(item)"
              />
            </div>
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-11 mt-2">
                {{ item.description }}
              </span>
              <component
                :is="renderInstruction(formatMessage(item.instruction, false))"
              />
              <span class="text-sm text-n-slate-11 font-medium mb-1">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED') }}
                {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
              </span>
            </div>
          </template>
        </SuggestedScenarios>
      </div>
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-if="canManageScenarios"
            v-model="bulkSelectedIds"
            :all-items="scenarios"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="requestBulkDeleteScenarios"
          >
            <template #actions>
              <Button
                :label="
                  $t(
                    'CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON'
                  )
                "
                sm
                ruby
                ghost
                class="!px-1.5"
                icon="i-lucide-trash"
                :disabled="isBulkDeletingScenarios || !bulkSelectedIds.size"
                :is-loading="isBulkDeletingScenarios"
                @click="requestBulkDeleteScenarios"
              />
            </template>
            <template #default-actions>
              <AddNewScenariosDialog @add="addScenario" />
            </template>
          </BulkSelectBar>
          <div
            v-if="
              scenarios.length &&
              (!canManageScenarios || bulkSelectedIds.size === 0)
            "
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('CAPTAIN.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <div v-if="scenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <ScenariosCard
            v-for="scenario in filteredScenarios"
            :id="scenario.id"
            :key="scenario.id"
            :title="scenario.title"
            :description="scenario.description"
            :instruction="scenario.instruction"
            :tools="scenario.tools"
            :history="historyForScenario(scenario.id)"
            :enabled="scenario.enabled !== false"
            :runtime-compatible="scenario.runtime_compatible !== false"
            :unavailable-tool-ids="scenario.unavailable_tool_ids || []"
            :action-type-label="scenarioActionTypeLabel(scenario)"
            :action-type-compatible="
              isScenarioActionTypeCompatible(scenario, actions)
            "
            :can-manage="canManageScenarios"
            :is-selected="bulkSelectedIds.has(scenario.id)"
            :selectable="
              canManageScenarios &&
              (hoveredCard === scenario.id || bulkSelectedIds.size > 0)
            "
            @select="handleRuleSelect"
            @delete="deleteScenario(scenario.id)"
            @update="updateScenario"
            @toggle-enabled="toggleScenarioEnabled"
            @open-tools="openTools"
            @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
          />
          <Button
            v-if="hasMoreHistory"
            class="self-center mt-2"
            :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.HISTORY.LOAD_MORE')"
            :is-loading="isLoadingMoreHistory"
            slate
            sm
            faded
            @click="loadMoreScenarioHistory"
          />
        </div>
      </div>
    </template>

    <Dialog
      v-if="canManageScenarios"
      ref="bulkDeleteDialog"
      type="alert"
      :title="t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.CONFIRM_TITLE')"
      :description="bulkDeleteDescription"
      :confirm-button-label="
        t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.CONFIRM_BUTTON')
      "
      :is-loading="isBulkDeletingScenarios"
      :disable-confirm-button="isBulkDeletingScenarios || !bulkSelectedIds.size"
      @confirm="confirmBulkDeleteScenarios"
    />
  </PageLayout>
</template>
